import { zodResolver } from '@hookform/resolvers/zod';
import { concat, find, map, propEq, reject } from 'ramda';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import BackAndNextNav, { IBackAndNextNavProps } from '@ui/molecules/BackAndNextNav';
import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import SearchableList from '@ui/templates/SearchableList';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { commonLabels } from '@utils/button';
import { exists } from '@utils/general';
import { convertToOrderedList } from '@utils/orderedList';

import { IListItem, IListItems } from '@folx-types/data';
import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IInsuranceProvider } from '@folx-types/insurance';

import theme from '@theme/FolxTheme';

import { schema } from './InsuranceProviderStep.data';

export interface IInsuranceProviderStepPageContent {
	pageHeader: string;
	pageInfo: string;
	selectInsuranceLabel: string;
}

interface IInsuranceProviderStepProps extends IFlowStepComponentProps {
	handleInsuranceSubmission: (provider: IInsuranceProvider) => void;
	pageContent: IInsuranceProviderStepPageContent;
	insuranceProviders?: IInsuranceProvider[];
}

const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const FullWidthNavigation = fullWidth<IBackAndNextNavProps>(BackAndNextNav);
const StickyNavigation = styled.div`
	position: sticky;
	bottom: 0;
	background: ${theme.colors.white};
	padding-bottom: ${theme.spacing.sm};
`;

const InsuranceProviderStep: FC<IInsuranceProviderStepProps> = ({
	pageContent,
	existingFormData,
	handleNavBack = () => {
		return;
	},
	handleInsuranceSubmission = () => {
		return;
	},
	insuranceProviders = [],
	stepKey,
}) => {
	const [submitting, setSubmitting] = useState(false);
	const ref = useRef<HTMLInputElement>(null);
	const { pageHeader, pageInfo, selectInsuranceLabel } = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			insuranceProvider: existingFormData?.insuranceProvider,
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		register,
		setValue,
		watch,
		formState: { errors },
	} = form;

	const selectedProvider = watch('insuranceProvider');

	const getPopularProviders = (providers: IListItems) => {
		let popularProviders: IListItems = [];
		let remainingProviders = providers;

		const cignaLabel = 'Cigna';
		const cigna = find<IListItem>(propEq('label', cignaLabel))(providers);
		if (exists(cigna)) {
			popularProviders = concat([cigna] as IListItems, popularProviders);
			remainingProviders = reject(propEq('label', cignaLabel), remainingProviders);
		}

		const bcbsLabel = 'Blue Cross and Blue Shield of Texas';
		const bcbs = find<IListItem>(propEq('label', bcbsLabel))(providers);
		if (exists(bcbs)) {
			popularProviders = concat([bcbs] as IListItems, popularProviders);
			remainingProviders = reject(propEq('label', bcbsLabel), remainingProviders);
		}

		const unitedLabel = 'UnitedHealthcare (UMR)';
		const united = find<IListItem>(propEq('label', unitedLabel))(providers);
		if (exists(united)) {
			popularProviders = concat([united] as IListItems, popularProviders);
			remainingProviders = reject(propEq('label', unitedLabel), remainingProviders);
		}

		return { popularProviders, remainingProviders };
	};

	const defaultOption: IListItem | null = useMemo(() => {
		const other = find<IInsuranceProvider>(propEq('name', 'Other'))(insuranceProviders);
		return other ? { value: other.id, label: other.name } : null;
	}, [insuranceProviders]);

	const insuranceOptions = useMemo(() => {
		const transformed = map<IInsuranceProvider, IListItem>((insuranceProvider) => {
			return { value: insuranceProvider.id, label: insuranceProvider.name };
		}, insuranceProviders);
		const { popularProviders, remainingProviders } = getPopularProviders(transformed);
		const providerList = convertToOrderedList(remainingProviders);

		return {
			Popular: popularProviders,
			...providerList,
		};
	}, [insuranceProviders]);

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		const selected = find<IInsuranceProvider>(
			(insuranceProvider) => insuranceProvider.id === data.insuranceProvider,
			insuranceProviders
		);

		if (selected) {
			handleInsuranceSubmission(selected);
		}
		setSubmitting(false);
	};

	return (
		<CenteredGridItem>
			<SpacedColumn gap="sm" marginBottom="lg">
				<HeadingText>{pageHeader}</HeadingText>
				<BasicText>{pageInfo}</BasicText>
			</SpacedColumn>
			<form onSubmit={handleSubmit(onSubmit)}>
				<SpacedColumn gap="lgxl" marginBottom="xxxl">
					<SearchableList
						{...register('insuranceProvider')}
						onChange={(e) => {
							setValue('insuranceProvider', e.target.value, { shouldValidate: true });
						}}
						error={!!errors?.insuranceProvider}
						errorLabel={errors?.insuranceProvider?.message}
						value={selectedProvider}
						defaultOption={defaultOption}
						data={insuranceOptions}
						ref={ref}
						searchPosition="xl"
						searchLabel={selectInsuranceLabel}
					/>
					<StickyNavigation>
						<FullWidthNavigation
							backOnClick={() => handleNavBack(stepKey)}
							submitLabel={commonLabels.next}
							submitting={submitting}
						/>
					</StickyNavigation>
				</SpacedColumn>
			</form>
		</CenteredGridItem>
	);
};

export default InsuranceProviderStep;
